/**
 * #1877 — saving the same chat to-do list twice.
 *
 * ── The bug ─────────────────────────────────────────────────────────────────
 *
 * `SaveTodosButton`'s `saved` flag is `useState(false)`, so after a reload an
 * already-saved message's button is back to its unsaved state, and nothing in
 * `handleSave` consults the database before writing. Tapping it a second time
 * — which is what someone does when they are not sure the first tap worked,
 * the exact instinct behind the report behind #1849/#1850 — runs the insert
 * again. For `destination.type === "new"` that creates a SECOND `todo_lists`
 * row with the same name; for the others it re-inserts every item.
 *
 * ── Why it is keyed on CONTENT, not on a message id ─────────────────────────
 *
 * ⚠️ There is no message id to key on. `Message` (ChatMessages.tsx) has no
 * `id` field, the assistant insert in Index.tsx mints none and selects none
 * back, and the history read does not even fetch it — so after a reload the
 * client cannot say which `messages` row a rendered message is. Giving
 * assistant messages durable ids is real work in the chat persistence
 * pipeline and is tracked separately; this module fixes the duplicate without
 * waiting on it, using what IS available: the item text and the destination.
 *
 * That is a deliberately weaker key, and the places it is weaker are stated in
 * `itemsNotAlreadySaved` below rather than left to be discovered.
 */

/**
 * The comparison key for one to-do line.
 *
 * Trimmed, lowercased, and inner whitespace collapsed, because the same list
 * re-rendered from a reloaded message is byte-identical but a human retyping
 * it is not — and the model itself is not guaranteed to reproduce spacing. An
 * exact-string compare would miss "Pack  swim bag" against "Pack swim bag" and
 * duplicate it.
 */
export function todoItemKey(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, " ");
}

/**
 * The items from this message that the destination does not already hold.
 *
 * Returns the ORIGINAL strings (not the keys), in their original order, so the
 * rows written keep her capitalisation and spacing.
 *
 * ⚠️ An empty result means "this save already landed" — the caller must then
 * confirm and navigate rather than writing. It does NOT mean "nothing to do":
 * silently doing nothing is how the first version of this bug felt to her.
 *
 * ⚠️ Where this key is deliberately weaker than a message id:
 *   - Two different messages proposing the same line resolve to one row. That
 *     is the right answer for a to-do list — she does not want "Pack swim bag"
 *     twice — but it means a second message can report "already saved".
 *   - If she EDITED the item after saving, its key changes and the original
 *     line is offered again. Adding it back is recoverable in one tap; the
 *     alternative (treating an edited row as still matching) would silently
 *     drop a line she asked for.
 *   - Case and spacing are ignored, so "milk" and "Milk" are one item.
 */
export function itemsNotAlreadySaved(
  items: readonly string[],
  existingItems: readonly string[],
): string[] {
  const have = new Set((existingItems ?? []).map(todoItemKey).filter(Boolean));
  const out: string[] = [];
  // Also collapses duplicates WITHIN one message, which a model can produce.
  const seen = new Set<string>();
  for (const raw of items ?? []) {
    const key = todoItemKey(raw ?? "");
    if (!key) continue;
    if (have.has(key) || seen.has(key)) continue;
    seen.add(key);
    out.push(raw);
  }
  return out;
}

/**
 * An existing list with this name, if there is one.
 *
 * ⚠️ Case-insensitive and whitespace-normalised, and this is the half that
 * actually stops the reported defect: a second tap re-creates the list by
 * NAME, and after a reload the fresh mount's list fetch already contains the
 * list the first tap made. Reusing it is strictly better than adding a second
 * row she then has to find and delete — nothing in the app merges two lists.
 *
 * The cost, stated: someone who genuinely wants two lists called "Groceries"
 * gets one. That is a far smaller harm than a silently duplicated list, and no
 * user has ever asked for the former.
 */
export function findListByName<T extends { id: string; name: string }>(
  lists: readonly T[] | null | undefined,
  name: string,
): T | null {
  const want = todoItemKey(name);
  if (!want) return null;
  for (const l of lists ?? []) {
    if (todoItemKey(l?.name ?? "") === want) return l;
  }
  return null;
}
